import {useInfiniteQuery} from '@tanstack/react-query';

import apiService from '../../transport/apiService';

/**
 * Standalone infinite query for global keyword search.
 *
 * Unlike useSearchLines (which reads from the shared SearchContext used by
 * the shop/department browsing flow), this hook accepts explicit params and
 * has zero coupling to SearchContext.  This eliminates the dispatch race
 * conditions that occurred when both SearchScreen and RangeProductListScreen
 * wrote to the same context.
 */
export function useGlobalSearchLines(storeId: string | null, keywords: string) {
  return useInfiniteQuery({
    queryKey: ['global-search', storeId, 'lines', {keywords: keywords || undefined}],
    queryFn: async ({pageParam = 1}) => {
      const response = await apiService.getDepartmentLines(
        {
          keywords: keywords || undefined,
          page: pageParam,
          page_size: 48,
        },
        // Strip the default `department` header from this request.
        // rangeStore.selectDepartment() sets a department header as an
        // axios default — without {unscoped: true}, global search would
        // inherit the Shop's department scope and return scoped results.
        {unscoped: true},
      );
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage?.meta) return undefined;
      const {page, page_count} = lastPage.meta;
      return page < page_count ? page + 1 : undefined;
    },
    enabled: !!storeId && !!keywords && keywords.length >= 3,
  });
}
